import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  Avatar,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { TOKENS, BRAND, avatarColors } from '../theme';
import ConfirmDeleteDialog from '../shared/ConfirmDeleteDialog';
import withShipment from '../../../withShipment';
import { viewOnlySelector } from '../../../redux/selectors/auth';

// Spec §2 — sessions for a selected employee, with the shift-day rule.
// A shift belongs to the day it STARTED, regardless of when it ended.
const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// ClockService.listEmployeeSession returns clockIn as a string in
// 'MM/dd/yyyy h:mm a' format (America/Chicago timezone). Parse that
// explicitly because `new Date(...)` is unreliable across browsers for
// this non-ISO format.
// Accepts EITHER separator. The server sends both shapes: "04/15/2026 12:21
// PM" from the session lists, and "09-11-2026" from getSessionReports, whose
// DateUtil.formatDateMMDDYYYY uses "MM-dd-yyyy". Matching only the slash form
// left the hyphen one falling through to new Date(), where it survived on
// Chrome leniency alone — Safari and Firefox return Invalid Date for that
// non-ISO string, which would print the weekday as an em dash.
export function parseUsDateString(s) {
  if (typeof s !== 'string') return null;
  const m =
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM)?)?/i.exec(
      s
    );
  if (!m) return null;
  let [, mo, day, yr, h, mn, ap] = m;
  h = h ? parseInt(h, 10) : 0;
  if (ap?.toUpperCase() === 'PM' && h < 12) h += 12;
  if (ap?.toUpperCase() === 'AM' && h === 12) h = 0;
  const d = new Date(
    parseInt(yr, 10),
    parseInt(mo, 10) - 1,
    parseInt(day, 10),
    h,
    mn ? parseInt(mn, 10) : 0
  );
  return isNaN(d.getTime()) ? null : d;
}

// Pull the start-time off whatever field the session carries
// (timeIn / clockIn / startedAt) and return the weekday name.
// Returns '—' if no parseable date can be found.
export function deriveAssignedDay(session) {
  if (!session) return '—';
  if (session.assignedDay) return session.assignedDay;
  const raw = session.timeIn || session.clockIn || session.startedAt;
  if (!raw) return '—';
  // 1) US format string like "04/15/2026 12:21 PM"
  const us = parseUsDateString(raw);
  if (us) return DAYS[us.getDay()];
  // 2) ISO string / Date object
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return DAYS[d.getDay()];
  // 3) MongoDB {$date: ...} wrapper
  const inner = raw?.$date;
  if (inner) {
    const d2 = new Date(inner);
    if (!isNaN(d2.getTime())) return DAYS[d2.getDay()];
  }
  return '—';
}

function EmployeeSessionsScreen({
  employee,
  sessions,
  sessionNumbers,
  onSelectSession,
  canForceClockOut = false,
  onForceClockOut,
  onDeleteSession,
  viewOnly,
  colorOf,
}) {
  // Session pending force-clock-out confirmation (null = dialog closed).
  const [pendingClockOut, setPendingClockOut] = useState(null);
  // Session pending delete confirmation (null = dialog closed).
  const [pendingDelete, setPendingDelete] = useState(null);

  const confirmDelete = () => {
    if (pendingDelete && typeof onDeleteSession === 'function') {
      onDeleteSession(pendingDelete);
    }
    setPendingDelete(null);
  };

  // A session is still open (employee forgot to clock out) when neither
  // clockOut nor timeOut is present.
  const isInProgress = (s) => !s.clockOut && !s.timeOut;

  const confirmForceClockOut = () => {
    if (pendingClockOut && typeof onForceClockOut === 'function') {
      onForceClockOut(pendingClockOut);
    }
    setPendingClockOut(null);
  };

  const employeeSessions = useMemo(() => {
    // Inline US-format-tolerant time parser; matches EmployeeSession.jsx.
    const parse = (s) => {
      if (typeof s === 'string') {
        const m =
          /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM)?)?/i.exec(
            s
          );
        if (m) {
          let [, mo, day, yr, h, mn, ap] = m;
          h = h ? parseInt(h, 10) : 0;
          if (ap?.toUpperCase() === 'PM' && h < 12) h += 12;
          if (ap?.toUpperCase() === 'AM' && h === 12) h = 0;
          return new Date(
            parseInt(yr, 10),
            parseInt(mo, 10) - 1,
            parseInt(day, 10),
            h,
            mn ? parseInt(mn, 10) : 0
          ).getTime();
        }
      }
      return new Date(s).getTime() || 0;
    };
    return (sessions || [])
      .filter((s) => s.name === employee?.name)
      .map((s) => ({ ...s, assignedDay: deriveAssignedDay(s) }))
      .sort(
        (a, b) => parse(b.timeIn || b.clockIn) - parse(a.timeIn || a.clockIn)
      );
  }, [sessions, employee]);

  return (
    <Box sx={{ pb: 2 }}>
      <Box sx={{ px: 2, pt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              ...avatarColors(employee?.userId, employee?.name, colorOf),
              fontWeight: 700,
              fontSize: 22,
            }}
          >
            {employee?.name?.charAt(0).toUpperCase() || '?'}
          </Avatar>
          <Box>
            <Typography
              sx={{ fontSize: 24, fontWeight: 700, color: TOKENS.slate900 }}
            >
              {employee?.name}
            </Typography>
            <Typography sx={{ fontSize: 13, color: TOKENS.slate500 }}>
              {employeeSessions.length}{' '}
              {employeeSessions.length === 1 ? 'session' : 'sessions'}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Typography
        sx={{
          px: 2,
          py: 1.5,
          fontSize: 13,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.slate500,
        }}
      >
        Sessions
      </Typography>
      <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {employeeSessions.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              color: TOKENS.slate400,
              fontSize: 13,
            }}
          >
            No sessions yet.
          </Box>
        )}
        {employeeSessions.map((s) => {
          const inProgress = isInProgress(s);
          return (
            <Box
              key={s.clockId}
              sx={{
                background: '#fff',
                border: `1px solid ${TOKENS.slate200}`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                <ButtonBase
                  onClick={() => onSelectSession(s)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    textAlign: 'left',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: TOKENS.slate900,
                        }}
                      >
                        Session #{sessionNumbers?.get(s.clockId) || '—'}
                      </Typography>
                      <Chip
                        size="small"
                        label={s.assignedDay || '—'}
                        sx={{
                          height: 22,
                          fontSize: 12,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          background: BRAND.track,
                          color: BRAND.ink,
                          border: `1px solid ${BRAND.line}`,
                        }}
                      />
                      {inProgress && (
                        <Chip
                          size="small"
                          label="In progress"
                          sx={{
                            height: 22,
                            fontSize: 12,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            background: TOKENS.emerald50,
                            color: TOKENS.emerald700,
                            border: `1px solid ${TOKENS.emerald200}`,
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      sx={{ fontSize: 13, color: TOKENS.slate500, mt: 0.5 }}
                    >
                      {s.clockIn || s.timeIn}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: TOKENS.slate400 }}>
                      → {s.clockOut || s.timeOut || 'In progress'}
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: TOKENS.slate400 }} />
                </ButtonBase>
                {/* Session delete and force-clock-out both mutate payroll
                    data. Removed at a VIEW-ONLY location — the server also
                    refuses forceClockOut outright (LocationAccess.
                    ALWAYS_BLOCKED). */}
                {typeof onDeleteSession === 'function' && !viewOnly && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      borderLeft: `1px solid ${TOKENS.slate100}`,
                    }}
                  >
                    <IconButton
                      aria-label="Delete session"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDelete(s);
                      }}
                      sx={{
                        mx: 0.5,
                        color: TOKENS.slate400,
                        '&:hover': {
                          color: TOKENS.red600,
                          background: TOKENS.red50,
                        },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
              {inProgress && canForceClockOut && !viewOnly && (
                <Box
                  sx={{
                    px: 1.5,
                    pb: 1.5,
                    pt: 0.5,
                    borderTop: `1px solid ${TOKENS.slate100}`,
                  }}
                >
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    startIcon={<LogoutIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingClockOut(s);
                    }}
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      fontWeight: 700,
                      color: TOKENS.red700,
                      borderColor: TOKENS.red200,
                      '&:hover': {
                        borderColor: TOKENS.red600,
                        background: TOKENS.red50,
                      },
                    }}
                  >
                    Clock Out &amp; Log Out
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Dialog
        open={Boolean(pendingClockOut)}
        onClose={() => setPendingClockOut(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: TOKENS.red100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <WarningAmberIcon sx={{ color: TOKENS.red600 }} />
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
              Clock out &amp; log out this employee?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: TOKENS.slate600 }}>
            This closes {employee?.name || 'the employee'}&apos;s open session
            (Session #{sessionNumbers?.get(pendingClockOut?.clockId) || '—'})
            using the current time and signs their account out of any active
            device.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingClockOut(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={confirmForceClockOut}
            variant="contained"
            startIcon={<LogoutIcon />}
            sx={{
              textTransform: 'none',
              background: TOKENS.red600,
              '&:hover': { background: TOKENS.red700 },
            }}
          >
            Clock Out &amp; Log Out
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete this session?"
        subtitle={`Session #${
          sessionNumbers?.get(pendingDelete?.clockId) || '—'
        } · ${employee?.name || ''}`}
      />
    </Box>
  );
}

const mapStateToProps = (state) => ({
  viewOnly: viewOnlySelector(state),
});

export default withShipment(
  { mapStateToProps, actionCreators: {} },
  EmployeeSessionsScreen
);
